import * as React from 'react';
import { useState } from 'react';
import { includes } from 'lodash';
import Overlay from 'vkid-ui/lib/Components/Overlay';
import { useDispatch, useSelector } from 'react-redux';
import { closeOverlay } from 'data/overlays/overlayHandler';
import { IStoreState } from '../../helpers/rootReducer';
import BuyBack from 'components/BuyBack/BuyBack';
import Block from 'vkid-ui/lib/Components/Block/index';
import Buttons from 'vkid-ui/lib/Components/Buttons';
import Button from 'vkid-ui/lib/Components/Button';
import ButtonsPrimary from 'vkid-ui/lib/Components/Buttons/Primary';
import ButtonsSecondary from 'vkid-ui/lib/Components/Buttons/Secondary';
import RenderHTML from 'vkid-ui/lib/Components/RenderHTML';
import { formatPrice } from '../../helpers/priceFormatHelper';
import { IBuyBackConfig, IBuyBackProduct } from 'components/BuyBack/interfaces/buyBackInterfaces';

interface IProps {
    config: IBuyBackConfig;
    buyBackProducts: IBuyBackProduct[];
    setBuyBackProducts: (buyBackProducts: IBuyBackProduct[]) => void;
    saveCallback?: (buyBackProducts?: IBuyBackProduct[]) => void;
    resetCallBack?: () => void;
}

const BuyBackModal = (props: IProps) => {
    const { config, buyBackProducts, setBuyBackProducts, saveCallback, resetCallBack } = props;

    const dispatch = useDispatch();
    const { openOverlays } = useSelector((state: IStoreState) => state.overlaysReducer);
    const [currentBuyBackProduct, setCurrentBuyBackProduct] = useState<IBuyBackProduct>();

    const isSubmitDisabled = () => {
        if (buyBackProducts.length) {
            return false;
        }

        if (currentBuyBackProduct && currentBuyBackProduct.model_price) {
            return false;
        }

        return true;
    };

    const close = () => {
        if (currentBuyBackProduct?.model_price) {
            setBuyBackProducts([...buyBackProducts, currentBuyBackProduct]);
        }

        if (saveCallback !== undefined) {
            currentBuyBackProduct
                ? saveCallback([...buyBackProducts, currentBuyBackProduct])
                : saveCallback(buyBackProducts);
        }

        setCurrentBuyBackProduct(undefined);
    };

    const closeAndReset = () => {
        if (resetCallBack !== undefined) {
            resetCallBack();
        }
    };

    return (
        <Overlay
            layout="focusview"
            isOpen={includes(openOverlays, 'buyBack')}
            doClose={() => {
                dispatch(closeOverlay({ name: 'all' }));
            }}
            zIndex={16000004}
            size="wide"
            buttonIcons={[
                {
                    icon: 'close',
                    onClick: () => {
                        close();
                        dispatch(closeOverlay({ name: 'all' }));
                    },
                },
            ]}
        >
            <Block>
                <h2 className="center">
                    <RenderHTML html={config.labels.title} nowrapper />
                </h2>
                <p className="center">
                    <RenderHTML html={config.blockHtml} nowrapper />
                </p>
                {!!buyBackProducts.length && (
                    <>
                        {buyBackProducts.map((buyBackProduct) => {
                            return (
                                <div className="tradein-product" key={buyBackProduct.unique_id}>
                                    <RenderHTML
                                        html={config.labels.productValueLabel
                                            .replace('%1', buyBackProduct.model_name)
                                            .replace('%2', formatPrice(buyBackProduct.model_price.toString(), true))}
                                        nowrapper
                                    />
                                    <Buttons>
                                        <Button
                                            layout="link"
                                            title={config.labels.change}
                                            onClick={() => {
                                                const curr = currentBuyBackProduct;
                                                setCurrentBuyBackProduct({ ...buyBackProduct });

                                                const newBuyBackProducts =
                                                    buyBackProducts.filter(
                                                        (item) => item.unique_id !== buyBackProduct.unique_id,
                                                    ) ?? [];

                                                if (curr && curr.model_price) {
                                                    newBuyBackProducts.push(curr);
                                                }

                                                setBuyBackProducts(newBuyBackProducts);
                                            }}
                                        />
                                        <Button
                                            layout="link"
                                            title={config.labels.remove}
                                            onClick={() => {
                                                setBuyBackProducts(
                                                    buyBackProducts.filter(
                                                        (item) => item.unique_id !== buyBackProduct.unique_id,
                                                    ),
                                                );
                                            }}
                                        />
                                    </Buttons>
                                </div>
                            );
                        })}
                    </>
                )}
                {buyBackProducts.length < 5 && (
                    <BuyBack
                        config={config}
                        currentBuyBackProduct={currentBuyBackProduct}
                        setCurrentBuyBackProduct={setCurrentBuyBackProduct}
                    />
                )}
                {!!currentBuyBackProduct?.model_price && buyBackProducts.length < 4 && (
                    <Buttons layout="centered">
                        <Button
                            layout="link"
                            icon="plus"
                            intent="success"
                            title={config.labels.addAnotherDevice}
                            onClick={() => {
                                if (!currentBuyBackProduct) {
                                    return;
                                }

                                setBuyBackProducts([...buyBackProducts, currentBuyBackProduct]);
                                setCurrentBuyBackProduct(undefined);
                            }}
                        />
                    </Buttons>
                )}
                <Buttons layout="equal">
                    <ButtonsSecondary>
                        <Button
                            title={config.labels.dontAddProducts}
                            intent="tertiary"
                            onClick={() => {
                                closeAndReset();
                                dispatch(closeOverlay({ name: 'all' }));
                            }}
                        />
                    </ButtonsSecondary>
                    <ButtonsPrimary>
                        <Button
                            title={config.labels.addProducts}
                            intent="primary"
                            onClick={() => {
                                close();
                                dispatch(closeOverlay({ name: 'all' }));
                            }}
                            disabled={isSubmitDisabled()}
                        />
                    </ButtonsPrimary>
                </Buttons>
            </Block>
        </Overlay>
    );
};
export default BuyBackModal;
